// FinKloz — GL Data Parser (browser-side)
// Parses GL export from Sage Intacct (CSV/Excel) for bank account reconciliation.
// Handles Sage multi-row headers and French/English labels.

import * as XLSX from 'xlsx';

// ─── Header detection patterns (Sage Intacct format) ───
const HEADER_PATTERNS = {
  date: /\b(date|entry\s*date|date\s*d['']?\s*[e\u00e9]criture|posted|transaction\s*date|date\s*effective)/i,
  description: /\b(description|memo|libell[e\u00e9]|detail|reference|ref)/i,
  journal: /\b(journal|jnl|source|type|j\.?e\.?)/i,
  currency: /\b(currency|curr|devise|ccy)/i,
  debit: /\b(debit|d[e\u00e9]bit)/i,
  credit: /\b(credit|cr[e\u00e9]dit)/i,
  amount: /^(amount|montant|net(?!\s*bal))$/i,
  txnAmount: /\b(txn\s*amt|txn\s*amount|transaction\s*amount|montant\s*trans|amount\s*\(usd\)|usd\s*amount|orig.*amount|foreign.*amount|source.*amount)/i,
  balance: /\b(balance|solde|running|cumul|ending)/i,
};

function parseAmount(val) {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return val;
  let s = String(val).trim();
  const isNeg = /^\(.*\)$/.test(s) || /^-/.test(s);
  s = s.replace(/[()$\s]/g, '');
  if (/^\d{1,3}(\s\d{3})*,\d{2}$/.test(s)) {
    s = s.replace(/\s/g, '').replace(',', '.');
  } else {
    s = s.replace(/,/g, '');
  }
  s = s.replace(/[^0-9.-]/g, '');
  const n = parseFloat(s) || 0;
  return isNeg ? -Math.abs(n) : n;
}

function parseDate(val) {
  if (!val) return null;
  const s = String(val).trim();
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  if (typeof val === 'number' && val > 25000 && val < 60000) {
    const d = new Date((val - 25569) * 86400 * 1000);
    return d.toISOString().split('T')[0];
  }
  return null;
}

function detectColumns(row) {
  const cols = { date: -1, description: -1, journal: -1, currency: -1, debit: -1, credit: -1, amount: -1, txnAmount: -1, balance: -1 };
  row.forEach((cell, i) => {
    const val = String(cell || '').trim();
    if (!val) return;
    for (const [key, pattern] of Object.entries(HEADER_PATTERNS)) {
      if (pattern.test(val) && cols[key] === -1) {
        cols[key] = i;
      }
    }
  });
  return cols;
}

// ─── Filter GL entries that net to zero within the same day ───
// Per user requirement: "Ignore GL transactions that add up to zero"
function filterZeroNetEntries(entries) {
  // Group by date
  const byDate = {};
  entries.forEach((e, idx) => {
    const key = e.date || 'unknown';
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push({ ...e, _origIdx: idx });
  });

  const removeSet = new Set();

  for (const [, group] of Object.entries(byDate)) {
    // Find pairs that cancel out (debit of one = credit of another)
    const used = new Set();
    for (let i = 0; i < group.length; i++) {
      if (used.has(i)) continue;
      const netI = (group[i].debit || 0) - (group[i].credit || 0);
      if (netI === 0) continue;
      for (let j = i + 1; j < group.length; j++) {
        if (used.has(j)) continue;
        const netJ = (group[j].debit || 0) - (group[j].credit || 0);
        if (Math.abs(netI + netJ) < 0.01) {
          used.add(i);
          used.add(j);
          removeSet.add(group[i]._origIdx);
          removeSet.add(group[j]._origIdx);
          break;
        }
      }
    }
  }

  return entries.filter((_, idx) => !removeSet.has(idx));
}

// ─── Filter out FX reversal entries (USD accounts) ───
// At month-start, last month's FX JE gets reversed. These entries exist in the GL
// but NOT on the bank statement, so they must be excluded from reconciliation.
export function filterFXReversals(entries) {
  return entries.filter(e => {
    const desc = (e.description || '').toLowerCase();
    const journal = (e.journal || '').toLowerCase();
    // FX reversal patterns: reversal entries, FX adjustments, exchange rate JEs
    const isFXReversal =
      /\b(revers|contre.?pass|annul)/i.test(desc) ||
      /\b(fx|f\.x\.|change|taux\s*de\s*change|exchange\s*rate|r[e\u00e9][e\u00e9]val)/i.test(desc) ||
      /\b(unrealized|non.?r[e\u00e9]alis[e\u00e9])/i.test(desc) ||
      (journal === 'gl' && /\b(revers|fx|change)\b/i.test(desc));
    return !isFXReversal;
  });
}

// ─── Main: parse GL export file ───
export async function parseGLExport(file, options = {}) {
  const ext = file.name.toLowerCase().split('.').pop();
  let rows;

  if (ext === 'csv') {
    const text = await file.text();
    rows = text.split(/\r?\n/).map(line => {
      const sep = line.includes(';') ? ';' : ',';
      return line.split(sep).map(cell => cell.replace(/^"(.*)"$/, '$1').trim());
    });
  } else if (['xls', 'xlsx', 'xlsm'].includes(ext)) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  } else {
    throw new Error(`Unsupported format: .${ext}. Use CSV, XLS, or XLSX.`);
  }

  // Find header row
  let cols = null;
  let dataStart = -1;

  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;
    const detected = detectColumns(row.map(c => String(c || '').trim()));
    if (detected.date !== -1 && (detected.debit !== -1 || detected.credit !== -1 || detected.amount !== -1)) {
      cols = detected;
      dataStart = i + 1;
      break;
    }
  }

  if (!cols || dataStart === -1) {
    // Return preview for manual column mapping
    const preview = rows.slice(0, 10).map(r => (r || []).map(c => String(c || '')));
    const err = new Error('Could not auto-detect GL columns. Please map them manually.');
    err.needsMapping = true;
    err.preview = preview;
    err.totalRows = rows.length;
    err._rows = rows; // keep full data for re-parse with manual mapping
    throw err;
  }

  // Parse entries
  const entries = [];
  let runningBalance = 0;

  for (let i = dataStart; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;

    const date = parseDate(cols.date !== -1 ? row[cols.date] : null);
    if (!date) continue;

    const description = cols.description !== -1 ? String(row[cols.description] || '').trim() : '';
    const journal = cols.journal !== -1 ? String(row[cols.journal] || '').trim() : '';
    const currency = cols.currency !== -1 ? String(row[cols.currency] || '').trim() : '';

    let debit = 0, credit = 0;

    if (cols.amount !== -1 && cols.debit === -1 && cols.credit === -1) {
      // Single amount column: positive = debit, negative = credit
      const amt = parseAmount(row[cols.amount]);
      if (amt >= 0) debit = amt; else credit = Math.abs(amt);
    } else {
      if (cols.debit !== -1) debit = Math.abs(parseAmount(row[cols.debit]));
      if (cols.credit !== -1) credit = Math.abs(parseAmount(row[cols.credit]));
    }

    // Read Txn Amount (original currency amount, always positive)
    // Sign comes from whether the CAD amount is in Debit or Credit column
    let txnAmount = null;
    if (cols.txnAmount !== -1) {
      txnAmount = Math.abs(parseAmount(row[cols.txnAmount]));
      // Apply sign: if CAD is in credit column, txn amount is negative (money out)
      if (txnAmount > 0 && credit > 0 && debit === 0) {
        txnAmount = txnAmount; // positive txn amount but it's a credit (money out of bank = negative for matching)
      }
    }

    // Read balance column if available
    let balance = null;
    if (cols.balance !== -1) {
      balance = parseAmount(row[cols.balance]);
    }

    // Skip zero entries
    if (debit === 0 && credit === 0 && balance === null) continue;
    // Skip subtotal rows
    if (/^(total|sous-total|subtotal|grand\s*total)/i.test(description)) continue;

    runningBalance = balance !== null ? balance : runningBalance + debit - credit;

    const txnD = txnAmount !== null && debit > 0 ? txnAmount : undefined;
    const txnC = txnAmount !== null && credit > 0 ? txnAmount : undefined;
    entries.push({
      id: `gl-${i}`,
      date, description, journal, currency, debit, credit,
      txnDebit: txnD, txnCredit: txnC,
      runningBalance: Math.round(runningBalance * 100) / 100,
    });
  }

  // Filter out zero-net entries
  const filtered = filterZeroNetEntries(entries);

  // Compute totals
  const totalDebit = filtered.reduce((s, e) => s + e.debit, 0);
  const totalCredit = filtered.reduce((s, e) => s + e.credit, 0);

  // Ending balance: use the last entry's running balance (from the Balance column)
  // or fall back to computed total
  const lastEntry = entries.length > 0 ? entries[entries.length - 1] : null;
  const endingBalance = lastEntry?.runningBalance ?? Math.round((totalDebit - totalCredit) * 100) / 100;

  // USD totals (from Txn Amount column) for USD account reconciliation
  const txnTotalDebit = filtered.reduce((s, e) => s + (e.txnDebit || 0), 0);
  const txnTotalCredit = filtered.reduce((s, e) => s + (e.txnCredit || 0), 0);
  const hasTxnAmounts = filtered.some(e => e.txnDebit !== undefined || e.txnCredit !== undefined);

  return {
    entries: filtered,
    totalEntries: filtered.length,
    filteredOut: entries.length - filtered.length,
    totalDebit: Math.round(totalDebit * 100) / 100,
    totalCredit: Math.round(totalCredit * 100) / 100,
    endingBalance,
    // USD-specific: original currency totals
    txnTotalDebit: hasTxnAmounts ? Math.round(txnTotalDebit * 100) / 100 : null,
    txnTotalCredit: hasTxnAmounts ? Math.round(txnTotalCredit * 100) / 100 : null,
    txnEndingBalance: hasTxnAmounts ? Math.round((txnTotalDebit - txnTotalCredit) * 100) / 100 : null,
  };
}

// ─── Parse with manual column mapping (fallback) ───
export function parseGLWithMapping(rows, mapping, headerRow = 0) {
  // mapping: { date: colIdx, description: colIdx, debit: colIdx, credit: colIdx, balance: colIdx, txnAmount: colIdx }
  const dataStart = headerRow + 1;
  const entries = [];
  let runningBalance = 0;

  for (let i = dataStart; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;

    const date = mapping.date >= 0 ? parseDate(row[mapping.date]) : null;
    if (!date) continue;

    const description = mapping.description >= 0 ? String(row[mapping.description] || '').trim() : '';
    const journal = mapping.journal >= 0 ? String(row[mapping.journal] || '').trim() : '';
    const currency = mapping.currency >= 0 ? String(row[mapping.currency] || '').trim() : '';

    let debit = 0, credit = 0;
    if (mapping.debit >= 0) debit = Math.abs(parseAmount(row[mapping.debit]));
    if (mapping.credit >= 0) credit = Math.abs(parseAmount(row[mapping.credit]));

    let txnAmount = null;
    if (mapping.txnAmount >= 0) {
      txnAmount = Math.abs(parseAmount(row[mapping.txnAmount]));
    }

    let balance = null;
    if (mapping.balance >= 0) balance = parseAmount(row[mapping.balance]);

    if (debit === 0 && credit === 0 && balance === null) continue;
    if (/^(total|sous-total|subtotal|grand\s*total)/i.test(description)) continue;

    runningBalance = balance !== null ? balance : runningBalance + debit - credit;

    entries.push({
      id: `gl-${i}`,
      date, description, journal, currency, debit, credit,
      txnDebit: txnAmount !== null && debit > 0 ? txnAmount : undefined,
      txnCredit: txnAmount !== null && credit > 0 ? txnAmount : undefined,
      runningBalance: Math.round(runningBalance * 100) / 100,
    });
  }

  const filtered = filterZeroNetEntries(entries);
  const totalDebit = filtered.reduce((s, e) => s + e.debit, 0);
  const totalCredit = filtered.reduce((s, e) => s + e.credit, 0);
  const lastEntry = entries.length > 0 ? entries[entries.length - 1] : null;
  const endingBalance = lastEntry?.runningBalance ?? Math.round((totalDebit - totalCredit) * 100) / 100;

  return {
    entries: filtered,
    totalEntries: filtered.length,
    filteredOut: entries.length - filtered.length,
    totalDebit: Math.round(totalDebit * 100) / 100,
    totalCredit: Math.round(totalCredit * 100) / 100,
    endingBalance,
  };
}
